import { Link, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { getConfig, getCriancaAtiva, getTodasOfertas, listarAlimentos } from "../../db/queries";
import { calcularDerivadosEquipe, disciplinasParaRetomar, painelResponsabilidade } from "../domain";
import { getNotasDaCrianca, listarProfissionais } from "../queries";
import { ESPECIALIDADE_LABEL } from "../../types";
import { diasAtras } from "../../lib/format";
import { FoodPhoto } from "../../ui/FoodPhoto";
import { EscadaMini } from "../../ui/EscadaMini";
import { EspecialidadeBadge } from "../ui/EspecialidadeBadge";
import { TimelineEquipe } from "../ui/TimelineEquipe";

/**
 * Elo · Hoje — painel da equipe da criança. Ação primária (registrar sessão),
 * estado da equipe (quem acompanha, disciplinas sem nota há X dias), prévia da
 * timeline interdisciplinar e o card reservado da escolha alimentar (ADR-13).
 */
export function HojeElo() {
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const cfg = useLiveQuery(getConfig);
  const profissionais = useLiveQuery(() => listarProfissionais(true)) ?? [];
  const ofertas = useLiveQuery(getTodasOfertas) ?? [];
  const alimentos = useLiveQuery(() => listarAlimentos(true)) ?? [];
  const notas = useLiveQuery(
    () => (crianca ? getNotasDaCrianca(crianca.id) : Promise.resolve([])),
    [crianca?.id],
  ) ?? [];

  const painel = crianca ? painelResponsabilidade(crianca.id, notas, ofertas, alimentos) : [];
  const cadencia = cfg?.elo_lembrete_dias ?? 14;
  const d = crianca
    ? calcularDerivadosEquipe(crianca.id, notas)
    : { n_notas: 0, disciplinas: [], dias_sem_nota: {}, ultima_sessao: null, crianca_id: "" };
  const retomar = disciplinasParaRetomar(d, cadencia);

  const nomePorProfissional = (id: string) =>
    profissionais.find((p) => p.id === id)?.nome ?? "profissional";

  return (
    <main className="tela">
      <div className="row-entre">
        <h1 className="tela-titulo">Elo{crianca ? ` · ${crianca.apelido}` : ""}</h1>
        <Link to="/elo/config" className="btn btn-fantasma" aria-label="configurações">
          ⚙️
        </Link>
      </div>

      {!crianca && (
        <button
          className="card linha-alimento"
          style={{ width: "100%", marginBottom: "var(--s4)" }}
          onClick={() => nav("/paciente")}
        >
          <div className="foto" aria-hidden>👤</div>
          <div className="crescer">
            <div className="nome">complete o cadastro do paciente</div>
            <div className="meta">o Elo organiza a equipe em torno dele</div>
          </div>
          <span aria-hidden>→</span>
        </button>
      )}

      {profissionais.filter((p) => !p.arquivado).length === 0 && (
        <div className="card boas-vindas">
          <div className="bv-titulo">a equipe num só lugar 🔗</div>
          <p className="bv-sub">como funciona, em 3 passos:</p>
          <ol className="bv-passos">
            <li>cadastre os <Link to="/elo/equipe">profissionais</Link> (fono, TO, psico, nutri).</li>
            <li><strong>registre cada sessão</strong>: objetivo, resultado na escada, orientações pra casa.</li>
            <li>veja a <strong>timeline interdisciplinar</strong> — uma criança, uma história.</li>
          </ol>
          <p className="bv-rodape">conecta e organiza o cuidado — não prescreve.</p>
        </div>
      )}

      <button
        className="btn btn-primario btn-bloco btn-grande"
        style={{ marginBottom: "var(--s3)" }}
        onClick={() => nav("/elo/registrar")}
      >
        🔗 registrar sessão
      </button>

      <button
        className="btn btn-bloco"
        style={{ marginBottom: "var(--s5)" }}
        onClick={() => nav("/elo/casa")}
      >
        🏠 pra fazer em casa
      </button>

      {d.n_notas > 0 && (
        <section className="panorama">
          <Link to="/elo/equipe" className="stat stat-seguro">
            <span className="stat-num">{d.disciplinas.length}</span>
            <span className="stat-rot">disciplinas</span>
          </Link>
          <Link to="/elo/timeline" className="stat">
            <span className="stat-num">{d.n_notas}</span>
            <span className="stat-rot">sessões</span>
          </Link>
          <Link to="/elo/timeline" className="stat stat-teste">
            <span className="stat-num">{retomar.length}</span>
            <span className="stat-rot">a retomar</span>
          </Link>
        </section>
      )}

      {/* painel de responsabilidade: alimentos em trabalho + de quem é a vez */}
      {painel.length > 0 && (
        <section className="secao">
          <h2 className="secao-titulo">alimentos em trabalho · de quem é a vez</h2>
          {painel.map((it) => {
            const a = alimentos.find((x) => x.id === it.alimento_id);
            const tambem = it.disciplinas.filter((e) => e !== it.responsavel);
            return (
              <button
                className="card linha-alimento"
                key={it.alimento_id}
                onClick={() => nav(`/elo/timeline?alimento=${it.alimento_id}`)}
              >
                <FoodPhoto fotoId={a?.fotos[0]} nome={a?.nome} categoria={a?.categoria} />
                <div className="crescer">
                  <div className="row-entre">
                    <div className="nome">{a?.nome ?? "alimento"}</div>
                    {it.responsavel ? (
                      <span className="row" style={{ gap: 4 }}>
                        <span className="meta">vez de</span>
                        <EspecialidadeBadge especialidade={it.responsavel} curto />
                      </span>
                    ) : (
                      <span className="meta">aguardando 1º degrau</span>
                    )}
                  </div>
                  <div className="evol">
                    <EscadaMini passo={it.degrau_atual} />
                    <span className="evol-meta">
                      {it.degrau_atual !== null ? `degrau ${it.degrau_atual}` : "—"} ·{" "}
                      {diasAtras(it.dias_desde_ultima)}
                    </span>
                  </div>
                  {tambem.length > 0 && (
                    <div className="meta" style={{ marginTop: 2 }}>
                      também acompanham: {tambem.map((e) => ESPECIALIDADE_LABEL[e].split(/[ (]/)[0]).join(", ")}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
          <p className="meta" style={{ color: "var(--text-suave)", marginTop: "var(--s2)" }}>
            "vez de" = quem destrava o degrau atual (0–4 TO · 5–9 fono). psico e nutri atuam em todos.
          </p>
        </section>
      )}

      {/* lembrete de coordenação: disciplinas sem nota há >= cadência */}
      {retomar.length > 0 && (
        <section className="secao">
          <h2 className="secao-titulo">faz tempo que não há nota de…</h2>
          <div className="card">
            <div className="chips">
              {retomar.map((r) => (
                <span className="chip" key={r.especialidade} title={`${r.dias} dias`}>
                  {ESPECIALIDADE_LABEL[r.especialidade]} · {diasAtras(r.dias)}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ADR-13: escolha alimentar compartilhada — reservada */}
      <section className="secao">
        <div className="card reservado">
          <div className="row-entre">
            <div className="crescer">
              <div className="nome">🍽️ objetivo alimentar compartilhado</div>
              <div className="meta">
                em breve a equipe poderá curar junto a próxima refeição. por ora, registre o
                alvo (alimento + degrau) na sessão.
              </div>
            </div>
            <span className="badge badge-teste">em estudo</span>
          </div>
        </div>
      </section>

      {/* prévia da timeline */}
      {notas.length > 0 && (
        <section className="secao">
          <div className="row-entre">
            <h2 className="secao-titulo">linha do tempo</h2>
            <Link to="/elo/timeline" className="btn btn-fantasma">ver tudo →</Link>
          </div>
          <TimelineEquipe
            notas={notas.slice(0, 4)}
            nomePorProfissional={nomePorProfissional}
            onAbrir={(id) => nav(`/elo/nota/${id}`)}
          />
        </section>
      )}
    </main>
  );
}
